import React, { useEffect, useMemo, useState } from 'react';
import './modernized_app.css';
import OffstationLocationDisplay from './OffstationLocationDisplay';
import { ResourcePlanningProvider } from './ResourcePlanning';
import {
  FlexibleDateTimeInput,
  G081DiscrepancyCard,
  G081RequirementCard,
  flexibleDateTimeInputToIsoOrThrow,
  toFlexibleDateTimeInputValue,
} from './StatusTab';
import { MILITARY_SHOP_OPTIONS } from './militaryOptions';

function scheduleWarningText(event) {
  return [event.scheduleWarning?.message, ...(event.resourceWarnings || []).map(row => row.message)].filter(Boolean).join(' | ');
}

const DEFAULT_EVENT_TYPES = [
  'Local Use',
  'Alert',
  'Exercise',
  'Deployment',
  'Depot',
  'TDY',
  'Spare',
  'Static',
  'Departing - Tail Swap',
  'Returning - Tail Swap',
  'Unavailable',
];

const DEFAULT_INSPECTION_SUBTYPES = [
  'PR', 'TH', 'BPO', 'PR/BPO', 'EOR', 'QT', 'HPO', 'PH',
  'ISO-MIN', 'ISO-MAJ', 'HSC', 'PDM', 'Other',
];

const TAIL_SWAP_DEPARTING_EVENT_TYPE = 'Departing - Tail Swap';
const TAIL_SWAP_RETURNING_EVENT_TYPE = 'Returning - Tail Swap';
const TAIL_SWAP_TBD_VALUE = 'TBD';
const TAIL_SWAP_EVENT_TYPES = new Set([
  TAIL_SWAP_DEPARTING_EVENT_TYPE,
  TAIL_SWAP_RETURNING_EVENT_TYPE,
]);
const LINKED_PLANNING_SOURCE_TYPES = new Set([
  'g081Requirement',
  'g081Discrepancy',
  'statusScheduled',
  'statusCustom',
]);
const G081_PLANNING_SOURCE_TYPES = new Set(['g081Requirement', 'g081Discrepancy']);
const ACFT_PLAN_FILTERS = [
  ['flying', 'Local Use', 'is-flying'],
  ['alert', 'Alert', 'is-alert'],
  ['static', 'Static', 'is-static'],
  ['custom', 'Custom Task', 'is-custom'],
  ['g081', 'G081 9188-6 drivers', 'is-g081'],
  ['g081-discrepancy', 'G081 8035/781K driver', 'is-g081-discrepancy'],
  ['return', 'Offstation Est Return', 'is-return'],
  ['tail-swap', 'Tail Swap', 'is-tail-swap'],
  ['mission', 'Deployment/TDY/exercise', 'is-mission'],
  ['maintenance', 'Depot / Spare / other plan', 'is-maintenance'],
  ['unavailable', 'Unavailable', 'is-unavailable'],
  ['overdue', 'Urgent / overdue', 'is-overdue'],
];

function sourceEntityId(event) {
  if (event?.sourceEntityId) return String(event.sourceEntityId);
  const [, id = ''] = String(event?.id || '').split(':');
  return id;
}

function dateKeyToFlexibleInput(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : '';
}

function linkedPlanningInputValue(timestamp, fallbackDate) {
  return timestamp ? toFlexibleDateTimeInputValue(timestamp) : dateKeyToFlexibleInput(fallbackDate);
}

function isTailSwapEventType(type) {
  return TAIL_SWAP_EVENT_TYPES.has(String(type || '').trim());
}

function tailSwapRoleFromType(type) {
  if (type === TAIL_SWAP_DEPARTING_EVENT_TYPE) return 'departing';
  if (type === TAIL_SWAP_RETURNING_EVENT_TYPE) return 'returning';
  return '';
}

function tailSwapLocationText(value, masked) {
  if (!value) return '';
  return masked ? 'Foreign OS' : value;
}

function tailSwapContextText(event) {
  if (!isTailSwapEventType(event?.eventType)) return '';
  const planLocation = tailSwapLocationText(event.planLocation, event.swapForeignOsMasked);
  return [
    event.swapWithTail ? `Swap with/for ${event.swapWithTail}` : '',
    event.swapWithLocation ? `Base ${event.swapWithLocation}` : '',
    planLocation ? `Projected location ${planLocation}` : '',
  ].filter(Boolean).join(' | ');
}

function normalizePlanTailDisplay(value) {
  const text = String(value || '').trim().toUpperCase();
  if (!text) return '';
  const displayMatch = text.match(/^(\d{2})\s*-\s*(\d{4})$/);
  if (displayMatch) return `${displayMatch[1]}-${displayMatch[2]}`;
  const digits = text.replace(/\D+/g, '');
  if (digits.length >= 6) return `${digits.slice(0, 2)}-${digits.slice(-4)}`;
  return text;
}

function planTailMatchKey(value) {
  return normalizePlanTailDisplay(value).toUpperCase();
}

function isValidSwapWithTail(value) {
  const normalized = normalizePlanTailDisplay(value);
  return normalized === TAIL_SWAP_TBD_VALUE || /^\d{2}-\d{4}$/.test(normalized);
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

function dateKey(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseDateKey(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function startOfWeekMonday(date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date, months) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function buildPlanMonth(monthStart) {
  const start = startOfMonth(monthStart);
  return {
    index: start.getMonth(),
    label: start.toLocaleDateString([], { month: 'short' }),
    start,
    end: new Date(start.getFullYear(), start.getMonth() + 1, 0),
    year: start.getFullYear(),
  };
}

function buildPlanMonthRange(start, end) {
  const months = [];
  let cursor = startOfMonth(start);
  const finalMonth = startOfMonth(end);
  while (cursor <= finalMonth) {
    months.push(buildPlanMonth(cursor));
    cursor = addMonths(cursor, 1);
  }
  return months;
}

function rangesOverlap(startA, endA, startB, endB) {
  return startA <= endB && endA >= startB;
}

function formatShortDate(value) {
  const date = parseDateKey(value);
  if (!date) return '--';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

function statusTone(status) {
  const normalized = String(status || '').toUpperCase();
  if (normalized.startsWith('FMC') || normalized === 'GAR') return 'mc';
  if (normalized.startsWith('P')) return 'p';
  if (normalized.startsWith('NMC')) return 'nmc';
  return 'neutral';
}

function isWeeklyFlyingPlanEvent(event) {
  return event?.sourceType === 'plan' && event?.eventType === 'Local Use';
}

function planSourceTone(event) {
  if (event?.sourceType === 'linkedTailSwap') return 'tail-swap';
  if (event?.sourceType === 'estimatedReturn') return 'return';
  if (['statusCustom', 'customTask', 'statusScheduled'].includes(event?.sourceType)) return 'custom';
  if (event?.sourceType === 'g081Requirement') return 'g081';
  if (event?.sourceType === 'g081Discrepancy' || event?.sourceType === 'g081781A') return 'g081-discrepancy';
  if (event?.sourceType && event.sourceType !== 'plan') return 'status';
  const type = String(event?.eventType || '').toLowerCase();
  if (isTailSwapEventType(event?.eventType)) return 'tail-swap';
  if (type === 'alert') return 'alert';
  if (type === 'static') return 'static';
  if (type === 'local use') return 'flying';
  if (['deployment', 'tdy', 'exercise'].includes(type)) return 'mission';
  if (['inspection', 'isochronal', 'depot'].includes(type)) return 'maintenance';
  if (type === 'unavailable') return 'unavailable';
  return 'manual';
}

function planFilterCategory(event) {
  const tone = planSourceTone(event);
  return tone === 'manual' || tone === 'status' ? 'maintenance' : tone;
}

function planNeedsAttention(event) {
  return Boolean(planRiskClass(event));
}

function planMatchesLegendSelection(event, categories) {
  return categories.some(category => (
    category === 'overdue'
      ? planNeedsAttention(event)
      : planFilterCategory(event) === category
  ));
}

function planSourceLabel(event) {
  if (event?.sourceLabel) return event.sourceLabel;
  if (event?.sourceType === 'estimatedReturn') return 'Status Est Return';
  if (event?.sourceType === 'plan' && isTailSwapEventType(event?.eventType)) return 'Tail Swap';
  if (event?.sourceType === 'plan') return 'ACFT Plan';
  return 'Linked source';
}

function planRiskClass(event) {
  const today = dateKey(new Date());
  const overdue = event?.originalDueDate && event.originalDueDate < today && !event.actualFinishDate;
  if (overdue) return 'is-overdue';
  if (event?.reviewRequired) return 'is-review-required';
  return '';
}

function planEventTitle(event) {
  const item = event?.event || event || {};
  return (
    item.noun
    || item.discrepancy
    || item.inspectionSubtypeOther
    || item.inspectionSubtype
    || item.eventType
    || item.title
    || item.sourceLabel
    || 'Plan item'
  );
}

function planWindowLabel(event) {
  return `${formatShortDate(event?.startDate)}-${formatShortDate(event?.endDate)}`;
}

function planDueLabel(event) {
  return event?.originalDueDate ? `Due ${formatShortDate(event.originalDueDate)}` : '';
}

function planDurationLabel(event) {
  if (event?.estimatedDurationHours === null || event?.estimatedDurationHours === undefined || event?.estimatedDurationHours === '') return '';
  const value = Number(event.estimatedDurationHours);
  if (!Number.isFinite(value) || value <= 0) return '';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} hr`;
}

function planAnchorEventId(event) {
  if (!event?.planningAnchorType || !event?.planningAnchorId) return '';
  if (event.planningAnchorType === 'estimated_return') return `estimated-return:${event.planningAnchorId}`;
  if (event.planningAnchorType === 'requirement') return `g081-requirement:${event.planningAnchorId}`;
  return '';
}

const PLAN_ADD_ROW_HEIGHT = 32;
const PLAN_TAIL_COLUMN_WIDTH = 120;
const PLAN_MONTH_MIN_WIDTH = 96;
const PLAN_MONTH_CARD_HEIGHT = 54;
const PLAN_ROW_TOP = 10;

function monthEventsForTail(tailEvents, month) {
  return tailEvents.filter(event => {
    const start = parseDateKey(event.startDate);
    const end = parseDateKey(event.endDate);
    return start && end && rangesOverlap(start, end, month.start, month.end);
  });
}

function visibleMonthSpan(event, months) {
  const start = parseDateKey(event?.startDate);
  const end = parseDateKey(event?.endDate);
  if (!start || !end || !months.length) return null;
  const startColumn = months.findIndex(month => rangesOverlap(start, end, month.start, month.end));
  if (startColumn === -1) return null;
  let endColumn = startColumn;
  for (let index = months.length - 1; index >= startColumn; index -= 1) {
    if (rangesOverlap(start, end, months[index].start, months[index].end)) {
      endColumn = index;
      break;
    }
  }
  return {
    start,
    end,
    startColumn,
    endColumn,
  };
}

function layoutTailPlanEvents(tailEvents, months) {
  const laneEnds = [];
  const items = (tailEvents || [])
    .map(event => {
      const span = visibleMonthSpan(event, months);
      if (!span) return null;
      return {
        event,
        startColumn: span.startColumn,
        endColumn: span.endColumn,
        startDate: event.startDate || '',
        endDate: event.endDate || '',
        title: planEventTitle(event),
      };
    })
    .filter(Boolean)
    .sort((left, right) => (
      left.startColumn - right.startColumn
      || left.endColumn - right.endColumn
      || String(left.startDate).localeCompare(String(right.startDate))
      || String(left.title).localeCompare(String(right.title))
    ))
    .map(item => {
      let lane = laneEnds.findIndex(endColumn => endColumn < item.startColumn);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(item.endColumn);
      } else {
        laneEnds[lane] = item.endColumn;
      }
      return {
        ...item,
        lane,
        gridColumn: `${item.startColumn + 1} / ${item.endColumn + 2}`,
        gridRow: `${lane + 1}`,
      };
    });
  return {
    items,
    byId: new Map(items.map(item => [item.event.id, item])),
    height: Math.max(
      118,
      PLAN_ROW_TOP + (Math.max(1, laneEnds.length) * PLAN_MONTH_CARD_HEIGHT) + PLAN_ADD_ROW_HEIGHT + 14,
    ),
  };
}

function linkedPlanConnectors(tailEvents, eventLayoutById) {
  const byId = new Map((tailEvents || []).map(event => [event.id, event]));
  const connectors = [];
  (tailEvents || [])
    .filter(event => event.sourceType === 'g081Requirement' && event.planningAnchorType)
    .forEach(event => {
      const anchor = byId.get(planAnchorEventId(event));
      if (!anchor) return;
      const anchorLayout = eventLayoutById.get(anchor.id);
      const childLayout = eventLayoutById.get(event.id);
      if (!anchorLayout || !childLayout) return;
      const title = `${planEventTitle(event)} moves with ${planEventTitle(anchor)}`;
      connectors.push({
        id: `${anchor.id}->${event.id}`,
        anchorId: anchor.id,
        childId: event.id,
        title,
      });
    });
  return connectors;
}

function planDependencyLabel(event, eventById) {
  const anchor = eventById.get(planAnchorEventId(event));
  if (!anchor) return '';
  return `Moves With: ${planEventTitle(anchor)}`;
}

function hsOsValue(row) {
  return String(row?.hsOs || row?.hs_os || '').trim().toUpperCase();
}

function aircraftScopeGroup(row) {
  const value = hsOsValue(row);
  if (value === 'OS') return 'offstation';
  if (value === 'HS') return 'homestation';
  return 'unknown';
}

function aircraftScopeLabel(row) {
  const value = hsOsValue(row);
  return value || 'HS/OS --';
}

function createEmptyForm(defaultTail = '', defaultType = 'Local Use') {
  return {
    id: '',
    tail: defaultTail,
    eventType: defaultType,
    inspectionSubtype: 'PR',
    inspectionSubtypeOther: '',
    originalDueDate: '',
    startDate: dateKey(new Date()),
    endDate: dateKey(new Date()),
    estimatedDurationHours: '',
    actualStartDate: '',
    actualFinishDate: '',
    planLocation: '',
    tailSwapRole: tailSwapRoleFromType(defaultType),
    swapWithTail: '',
    swapWithLocation: '',
    swapForeignOsMasked: false,
    remarks: '',
  };
}

export default function ACFTPlansTab({ api, user, selectedBase, globalView }) {
  const currentYear = new Date().getFullYear();
  const [planYear, setPlanYear] = useState(currentYear);
  const [selectedWeekStart, setSelectedWeekStart] = useState(dateKey(startOfWeekMonday(new Date())));
  const [events, setEvents] = useState([]);
  const [statusRows, setStatusRows] = useState([]);
  const [commanders, setCommanders] = useState([]);
  const [eventTypes, setEventTypes] = useState(DEFAULT_EVENT_TYPES);
  const [inspectionSubtypes, setInspectionSubtypes] = useState(DEFAULT_INSPECTION_SUBTYPES);
  const [globalSwapTailOptions, setGlobalSwapTailOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [signoff, setSignoff] = useState(null);
  const [reviewChanges, setReviewChanges] = useState([]);
  const [hasChangesSinceSignoff, setHasChangesSinceSignoff] = useState(false);
  const [canSignWeekPlan, setCanSignWeekPlan] = useState(false);
  const [canManageAcftPlans, setCanManageAcftPlans] = useState(false);
  const [form, setForm] = useState(() => createEmptyForm());
  const [editorOpen, setEditorOpen] = useState(false);
  const [linkedTailSwapEdit, setLinkedTailSwapEdit] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [search, setSearch] = useState('');
  const [activePlanEventId, setActivePlanEventId] = useState('');
  const [linkedPlanningEvent, setLinkedPlanningEvent] = useState(null);
  const [linkedPlanningDraft, setLinkedPlanningDraft] = useState({ plannedStartAt: '', plannedFinishAt: '' });
  const [linkedPlanningSaving, setLinkedPlanningSaving] = useState(false);
  const [linkedPlanningFeedback, setLinkedPlanningFeedback] = useState('');
  const [linkedPlanningMode, setLinkedPlanningMode] = useState('quick');
  const [linkedPlanningDetail, setLinkedPlanningDetail] = useState({ loading: false, error: '', data: null });
  const [activePlanCategories, setActivePlanCategories] = useState([]);

  const yearStart = `${planYear}-01-01`;
  const yearEnd = `${planYear + 1}-06-30`;

  const loadPlans = async () => {
    try {
      const res = await api.get('/get-acft-plans', {
        params: {
          email: user.email,
          location: selectedBase,
          global: user.is_superuser && globalView ? true : undefined,
          startDate: yearStart,
          endDate: yearEnd,
          weekStart: selectedWeekStart,
        },
      });
      setEvents(res.data.data || []);
      setStatusRows(res.data.statusRows || []);
      setCommanders(res.data.commanders || []);
      setEventTypes(res.data.eventTypes?.length ? res.data.eventTypes : DEFAULT_EVENT_TYPES);
      setInspectionSubtypes(res.data.inspectionSubtypes?.length ? res.data.inspectionSubtypes : DEFAULT_INSPECTION_SUBTYPES);
      setGlobalSwapTailOptions(res.data.swapTailOptions || []);
      setLocationOptions(res.data.locationOptions || []);
      setSignoff(res.data.signoff || null);
      setReviewChanges(res.data.reviewChanges || []);
      setHasChangesSinceSignoff(Boolean(res.data.hasChangesSinceSignoff));
      setCanSignWeekPlan(Boolean(res.data.canSignWeekPlan));
      setCanManageAcftPlans(Boolean(res.data.canManageAcftPlans));
    } catch (err) {
      console.error('Failed to load aircraft plans', err);
      setFeedback(err.response?.data?.message || 'Failed to load aircraft plans.');
    }
  };

  useEffect(() => {
    if (user?.email && selectedBase) loadPlans();
  }, [api, user?.email, user?.is_superuser, selectedBase, globalView, planYear, selectedWeekStart]);

  const statusByTail = useMemo(() => {
    const map = new Map();
    statusRows.forEach(row => map.set(String(row.tail || '').trim(), row));
    return map;
  }, [statusRows]);

  const tails = useMemo(() => {
    const set = new Set();
    statusRows.forEach(row => row.tail && set.add(row.tail));
    events.forEach(event => event.tail && set.add(event.tail));
    const normalizedSearch = search.trim().toLowerCase();
    return Array.from(set)
      .filter(tail => !normalizedSearch || tail.toLowerCase().includes(normalizedSearch))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [events, statusRows, search]);

  const tailScopedEvents = useMemo(() => {
    const displayedTails = new Set(tails);
    return events.filter(event => event.tail && displayedTails.has(event.tail));
  }, [events, tails]);

  const swapTailOptions = useMemo(() => {
    const set = new Set([TAIL_SWAP_TBD_VALUE]);
    const source = globalSwapTailOptions.length ? globalSwapTailOptions : statusRows.map(row => row.tail);
    source.forEach(tail => {
      const normalized = normalizePlanTailDisplay(tail);
      if (normalized && /^\d{2}-\d{4}$/.test(normalized)) set.add(normalized);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [globalSwapTailOptions, statusRows]);

  const groupedTails = useMemo(() => {
    const groups = [
      { key: 'homestation', label: 'Homestation Aircraft', tails: [] },
      { key: 'offstation', label: 'Offstation Aircraft', tails: [] },
      { key: 'unknown', label: 'HS/OS Not Set', tails: [] },
    ];
    const byKey = new Map(groups.map(group => [group.key, group]));
    tails.forEach(tail => {
      const status = statusByTail.get(tail);
      byKey.get(aircraftScopeGroup(status) || 'unknown').tails.push(tail);
    });
    return groups.filter(group => group.tails.length);
  }, [tails, statusByTail]);

  const months = useMemo(() => {
    const windowStart = new Date(planYear, 0, 1);
    const windowEnd = new Date(planYear + 1, 5, 30);
    const spans = tailScopedEvents
      .map(event => ({
        start: parseDateKey(event.startDate),
        end: parseDateKey(event.endDate),
      }))
      .filter(span => span.start && span.end)
      .map(span => ({
        start: span.start < windowStart ? windowStart : span.start,
        end: span.end > windowEnd ? windowEnd : span.end,
      }))
      .filter(span => span.start <= span.end);

    if (!spans.length) {
      return buildPlanMonthRange(windowStart, addMonths(windowStart, 5));
    }

    const firstMonth = spans.reduce((earliest, span) => (span.start < earliest ? span.start : earliest), spans[0].start);
    const lastMonth = spans.reduce((latest, span) => (span.end > latest ? span.end : latest), spans[0].end);
    return buildPlanMonthRange(firstMonth, lastMonth);
  }, [tailScopedEvents, planYear]);
  const monthlyGridStyle = useMemo(() => ({
    '--acft-plan-month-count': months.length,
    '--acft-plan-grid-min-width': `${months.length * PLAN_MONTH_MIN_WIDTH}px`,
    '--acft-plan-table-min-width': `${PLAN_TAIL_COLUMN_WIDTH + (months.length * PLAN_MONTH_MIN_WIDTH)}px`,
  }), [months.length]);

  const monthYearBands = useMemo(() => months.reduce((bands, month) => {
    const latest = bands[bands.length - 1];
    if (latest?.year === month.year) {
      latest.count += 1;
    } else {
      bands.push({ year: month.year, count: 1 });
    }
    return bands;
  }, []), [months]);

  const weekDays = useMemo(() => {
    const start = parseDateKey(selectedWeekStart) || startOfWeekMonday(new Date());
    return Array.from({ length: 7 }, (_, index) => addDays(start, index));
  }, [selectedWeekStart]);

  const weekEndKey = dateKey(weekDays[6]);

  const weeklyEvents = useMemo(() => tailScopedEvents.filter(event => {
    const start = parseDateKey(event.startDate);
    const end = parseDateKey(event.endDate);
    return start && end && rangesOverlap(start, end, weekDays[0], weekDays[6]);
  }), [tailScopedEvents, weekDays]);

  const weeklyFlyingEvents = useMemo(() => weeklyEvents.filter(isWeeklyFlyingPlanEvent), [weeklyEvents]);

  const conflicts = useMemo(() => weeklyFlyingEvents.filter(event => {
    const status = statusByTail.get(event.tail);
    return status && statusTone(status.status) === 'nmc';
  }), [weeklyFlyingEvents, statusByTail]);

  const savePlan = async () => {
    if (!canManageAcftPlans) {
      setFeedback('You have read-only access to aircraft plans.');
      return;
    }
    const normalizedSwapWithTail = normalizePlanTailDisplay(form.swapWithTail);
    if (formIsTailSwap) {
      if (!isValidSwapWithTail(normalizedSwapWithTail)) {
        setFeedback('Swap With/For Tail must use YY-#### format or TBD.');
        setForm(current => ({ ...current, swapWithTail: normalizedSwapWithTail }));
        return;
      }
      if (planTailMatchKey(normalizedSwapWithTail) === planTailMatchKey(form.tail)) {
        setFeedback('Swap With/For Tail must be different from the plan tail.');
        setForm(current => ({ ...current, swapWithTail: normalizedSwapWithTail }));
        return;
      }
    }
    setSaving(true);
    setFeedback('');
    try {
      const res = await api.post('/save-acft-plan', {
        ...form,
        swapWithTail: formIsTailSwap ? normalizedSwapWithTail : '',
        email: user.email,
        location: selectedBase,
      });
      setFeedback(res.data.message || 'Aircraft plan saved.');
      setForm(createEmptyForm(form.tail, form.eventType));
      setLinkedTailSwapEdit(null);
      setEditorOpen(false);
      await loadPlans();
    } catch (err) {
      console.error('Failed to save aircraft plan', err);
      setFeedback(err.response?.data?.message || 'Failed to save aircraft plan.');
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (event) => {
    if (!canManageAcftPlans) {
      setFeedback('You have read-only access to aircraft plans.');
      return;
    }
    if (!window.confirm(`Delete ${event.tail} ${event.eventType} plan?`)) return;
    try {
      await api.post('/delete-acft-plan', {
        id: event.id,
        email: user.email,
        location: selectedBase,
      });
      setFeedback('Aircraft plan deleted.');
      if (String(form.id) === String(event.id)) {
        setForm(createEmptyForm());
        setEditorOpen(false);
      }
      await loadPlans();
    } catch (err) {
      console.error('Failed to delete aircraft plan', err);
      setFeedback(err.response?.data?.message || 'Failed to delete aircraft plan.');
    }
  };

  const signWeek = async () => {
    setSigning(true);
    setFeedback('');
    try {
      const res = await api.post('/sign-acft-week-plan', {
        email: user.email,
        location: selectedBase,
        weekStart: selectedWeekStart,
      });
      setSignoff(res.data.signoff);
      setReviewChanges([]);
      setHasChangesSinceSignoff(false);
      setFeedback(res.data.message || 'Weekly plan signed.');
    } catch (err) {
      console.error('Failed to sign weekly plan', err);
      setFeedback(err.response?.data?.message || 'Failed to sign weekly plan.');
    } finally {
      setSigning(false);
    }
  };

  const openEventEditor = (event) => {
    const isLinkedTailSwap = event?.sourceType === 'linkedTailSwap';
    if (!canManageAcftPlans || (event?.sourceType !== 'plan' && !isLinkedTailSwap)) return;
    const editableEvent = isLinkedTailSwap ? event.sourcePlanEvent : event;
    if (!editableEvent?.id) {
      setFeedback('The source tail-swap record is unavailable. Refresh ACFT Plans and try again.');
      return;
    }
    setLinkedTailSwapEdit(isLinkedTailSwap ? {
      viewedTail: event.tail || '',
      sourceTail: editableEvent.tail || '',
    } : null);
    setForm({
      id: editableEvent.id,
      tail: editableEvent.tail || '',
      eventType: editableEvent.eventType || 'Local Use',
      inspectionSubtype: editableEvent.inspectionSubtype || 'PR',
      inspectionSubtypeOther: editableEvent.inspectionSubtypeOther || '',
      originalDueDate: editableEvent.originalDueDate || '',
      startDate: editableEvent.startDate || dateKey(new Date()),
      endDate: editableEvent.endDate || editableEvent.startDate || dateKey(new Date()),
      estimatedDurationHours: editableEvent.estimatedDurationHours ?? '',
      actualStartDate: editableEvent.actualStartDate || '',
      actualFinishDate: editableEvent.actualFinishDate || '',
      planLocation: editableEvent.planLocation || '',
      tailSwapRole: editableEvent.tailSwapRole || tailSwapRoleFromType(editableEvent.eventType),
      swapWithTail: editableEvent.swapWithTail || '',
      swapWithLocation: editableEvent.swapWithLocation || '',
      swapForeignOsMasked: Boolean(editableEvent.swapForeignOsMasked),
      remarks: editableEvent.remarks || '',
    });
    if (editableEvent.startDate) setSelectedWeekStart(dateKey(startOfWeekMonday(parseDateKey(editableEvent.startDate))));
    setEditorOpen(true);
  };

  const openNewEventEditor = (overrides = {}) => {
    if (!canManageAcftPlans) return;
    setLinkedTailSwapEdit(null);
    const defaultType = overrides.eventType || (eventTypes.includes('Local Use') ? 'Local Use' : eventTypes[0]) || 'Local Use';
    setForm({
      ...createEmptyForm(overrides.tail || '', defaultType),
      ...overrides,
      id: '',
    });
    setEditorOpen(true);
  };

  const openMonthEditor = (tail, month) => {
    openNewEventEditor({
      tail,
      startDate: dateKey(month.start),
      endDate: dateKey(month.end),
    });
  };

  const closeLinkedPlanningEditor = () => {
    if (linkedPlanningSaving) return;
    setLinkedPlanningEvent(null);
    setLinkedPlanningFeedback('');
    setLinkedPlanningMode('quick');
    setLinkedPlanningDetail({ loading: false, error: '', data: null });
  };

  const openLinkedPlanningEditor = event => {
    if (!event?.canEditPlanning || !LINKED_PLANNING_SOURCE_TYPES.has(event.sourceType)) return;
    setLinkedPlanningEvent(event);
    setLinkedPlanningDraft({
      plannedStartAt: linkedPlanningInputValue(event.plannedStartAt, event.startDate),
      plannedFinishAt: linkedPlanningInputValue(event.plannedFinishAt, event.endDate),
    });
    setLinkedPlanningFeedback('');
    setLinkedPlanningMode('quick');
    setLinkedPlanningDetail({ loading: false, error: '', data: null });
  };

  const applyLinkedPlanningWindow = (eventId, plannedStartAt, plannedFinishAt) => {
    const startDate = dateKey(new Date(plannedStartAt));
    const endDate = dateKey(new Date(plannedFinishAt));
    setEvents(current => current.map(event => (
      event.id === eventId
        ? { ...event, plannedStartAt, plannedFinishAt, startDate, endDate }
        : event
    )));
  };

  const saveLinkedPlanningWindow = async event => {
    event.preventDefault();
    if (!linkedPlanningEvent || linkedPlanningSaving) return;
    setLinkedPlanningSaving(true);
    setLinkedPlanningFeedback('Saving planning dates and refreshing readiness...');
    try {
      const plannedStartAt = flexibleDateTimeInputToIsoOrThrow(
        'Planned start date',
        linkedPlanningDraft.plannedStartAt,
        { required: true },
      );
      const plannedFinishAt = flexibleDateTimeInputToIsoOrThrow(
        'Planned finish date',
        linkedPlanningDraft.plannedFinishAt,
        { required: true },
      );
      if (new Date(plannedFinishAt) < new Date(plannedStartAt)) {
        throw new Error('Planned finish must be on or after planned start.');
      }
      const id = sourceEntityId(linkedPlanningEvent);
      const payload = {
        email: user.email,
        location: selectedBase,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        plannedStartAt,
        plannedFinishAt,
      };
      if (linkedPlanningEvent.sourceType === 'g081Requirement') {
        await api.patch(`/g081-imports/requirements/${id}`, payload);
      } else if (linkedPlanningEvent.sourceType === 'g081Discrepancy') {
        await api.patch(`/g081-imports/discrepancies/${id}`, payload);
      } else if (linkedPlanningEvent.sourceType === 'statusScheduled') {
        await api.post('/update-aircraft-scheduled-event', { ...payload, id });
      } else if (linkedPlanningEvent.sourceType === 'statusCustom') {
        await api.patch('/update-aircraft-custom-task-planning', { ...payload, id });
      } else {
        throw new Error('This task source does not support planning-date edits.');
      }
      applyLinkedPlanningWindow(linkedPlanningEvent.id, plannedStartAt, plannedFinishAt);
      setFeedback(`${planEventTitle(linkedPlanningEvent)} planning dates saved.`);
      setLinkedPlanningEvent(null);
      setLinkedPlanningFeedback('');
      setLinkedPlanningMode('quick');
      setLinkedPlanningDetail({ loading: false, error: '', data: null });
      loadPlans();
    } catch (error) {
      setLinkedPlanningFeedback(error?.response?.data?.message || error?.message || 'Planning dates could not be saved.');
    } finally {
      setLinkedPlanningSaving(false);
    }
  };

  const openFullLinkedPlanning = async () => {
    if (!linkedPlanningEvent || !G081_PLANNING_SOURCE_TYPES.has(linkedPlanningEvent.sourceType)) return;
    if (!linkedPlanningEvent.aircraftStatusId) {
      setLinkedPlanningFeedback('Aircraft source details are unavailable for this task.');
      return;
    }
    setLinkedPlanningMode('full');
    setLinkedPlanningDetail({ loading: true, error: '', data: null });
    try {
      const params = { email: user.email, location: selectedBase };
      const [response, laborResponse] = await Promise.all([
        api.get(`/g081-imports/aircraft/${linkedPlanningEvent.aircraftStatusId}`, { params }),
        api.get('/g081-imports/labor-templates', { params }),
      ]);
      setLinkedPlanningDetail({
        loading: false,
        error: '',
        data: {
          ...response.data,
          laborProfiles: laborResponse.data?.templates || [],
          laborPools: laborResponse.data?.laborPools || [],
        },
      });
    } catch (error) {
      setLinkedPlanningDetail({
        loading: false,
        error: error?.response?.data?.message || 'Full planning details could not be loaded.',
        data: null,
      });
    }
  };

  const updateFullG081Planning = async (record, values) => {
    if (!linkedPlanningEvent) return;
    const resource = linkedPlanningEvent.sourceType === 'g081Requirement' ? 'requirements' : 'discrepancies';
    await api.patch(`/g081-imports/${resource}/${record.id}`, {
      email: user.email,
      location: selectedBase,
      ...values,
    });
    if (values.plannedStartAt && values.plannedFinishAt) {
      applyLinkedPlanningWindow(linkedPlanningEvent.id, values.plannedStartAt, values.plannedFinishAt);
    }
  };

  const finishFullLinkedPlanning = () => {
    if (!linkedPlanningEvent) return;
    setFeedback(`${planEventTitle(linkedPlanningEvent)} planning saved.`);
    setLinkedPlanningEvent(null);
    setLinkedPlanningMode('quick');
    setLinkedPlanningDetail({ loading: false, error: '', data: null });
    loadPlans();
  };

  const fullPlanningRequirements = linkedPlanningDetail.data?.requirements || [];
  const fullPlanningRecord = linkedPlanningEvent?.sourceType === 'g081Requirement'
    ? fullPlanningRequirements.find(item => String(item.id) === sourceEntityId(linkedPlanningEvent))
    : (linkedPlanningDetail.data?.delayedDiscrepancies || []).find(item => String(item.id) === sourceEntityId(linkedPlanningEvent));
  const fullPlanningAircraft = linkedPlanningDetail.data?.aircraft || {};
  const fullPlanningAnchorOptions = [
    ...(fullPlanningAircraft.id ? [{
      value: `estimated_return|${fullPlanningAircraft.id}|estimatedReturnAt`,
      label: 'Estimated Return',
      anchorId: fullPlanningAircraft.id,
    }] : []),
    ...fullPlanningRequirements.flatMap(requirement => [
      requirement.dueAt ? { value: `requirement|${requirement.id}|dueAt`, label: `${requirement.noun || requirement.wuc || 'Requirement'} due date`, anchorId: requirement.id } : null,
      requirement.plannedStartAt ? { value: `requirement|${requirement.id}|plannedStartAt`, label: `${requirement.noun || requirement.wuc || 'Requirement'} planned start`, anchorId: requirement.id } : null,
      requirement.plannedFinishAt ? { value: `requirement|${requirement.id}|plannedFinishAt`, label: `${requirement.noun || requirement.wuc || 'Requirement'} planned finish`, anchorId: requirement.id } : null,
    ].filter(Boolean)),
  ];
  const fullPlanningCoverageOptions = fullPlanningRequirements.map(requirement => ({
    value: `requirement|${requirement.id}`,
    entityType: 'requirement',
    entityId: requirement.id,
    shortLabel: requirement.noun || requirement.wuc || '9188-6 task',
    label: [requirement.noun || requirement.wuc || '9188-6 task', requirement.wuc].filter(Boolean).join(' | '),
    plannedStartAt: requirement.plannedStartAt || '',
    plannedFinishAt: requirement.plannedFinishAt || '',
  }));

  const formatChangeDates = (change) => {
    if (!change.startDate && !change.endDate) return 'Date window unavailable';
    return `${formatShortDate(change.startDate)} - ${formatShortDate(change.endDate)}`;
  };
  const formIsTailSwap = isTailSwapEventType(form.eventType);
  const updateEventType = (eventType) => {
    setForm(current => {
      const nextIsTailSwap = isTailSwapEventType(eventType);
      return {
        ...current,
        eventType,
        endDate: nextIsTailSwap ? current.startDate : current.endDate,
        tailSwapRole: tailSwapRoleFromType(eventType),
        swapWithTail: nextIsTailSwap ? current.swapWithTail : '',
        swapWithLocation: nextIsTailSwap ? current.swapWithLocation : '',
        swapForeignOsMasked: nextIsTailSwap ? current.swapForeignOsMasked : false,
      };
    });
  };
  const updateSwapDate = (value) => setForm(current => ({ ...current, startDate: value, endDate: value }));

  return (
    <div className="acft-plans">
      <header className="acft-plans__header ops-page-header">
        <div className="ops-page-header__copy">
          <h2 className="ops-page-header__title">Aircraft Planning</h2>
        </div>
        <div className="acft-plans__tools">
          <label>
            <span>Plan Year</span>
            <input type="number" min="2020" max="2100" value={planYear} onChange={event => setPlanYear(Number(event.target.value) || currentYear)} />
          </label>
          <label>
            <span>Search Tail</span>
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Tail #" />
          </label>
          {canManageAcftPlans ? (
            <button type="button" className="acft-plans__add-button" onClick={() => openNewEventEditor()}>+ Add Plan Event</button>
          ) : null}
        </div>
      </header>

      {feedback ? <div className="status-notification">{feedback}</div> : null}

      {linkedPlanningEvent ? (
        <div className="acft-plan-modal-backdrop" role="presentation" onMouseDown={closeLinkedPlanningEditor}>
          <section
            className={`acft-plan-linked-editor reports-panel${linkedPlanningMode === 'full' ? ' is-full-planning' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="acft-plan-linked-editor-title"
            aria-busy={linkedPlanningSaving || linkedPlanningDetail.loading}
            onMouseDown={event => event.stopPropagation()}
          >
            <div className="reports-panel__header">
              <div>
                <h3 id="acft-plan-linked-editor-title">{linkedPlanningMode === 'full' ? 'Full Task Planning' : 'Plan Task Dates'}</h3>
                <span>{linkedPlanningEvent.tail} | {planSourceLabel(linkedPlanningEvent)}</span>
              </div>
              <button type="button" className="acft-plan-modal__close" onClick={closeLinkedPlanningEditor} disabled={linkedPlanningSaving} aria-label="Close task planning editor">x</button>
            </div>
            <div className="acft-plan-linked-editor__identity">
              <strong>{planEventTitle(linkedPlanningEvent)}</strong>
              <span>Due: {linkedPlanningEvent.originalDueDate ? formatShortDate(linkedPlanningEvent.originalDueDate) : 'Not provided'}</span>
            </div>
            {linkedPlanningMode === 'quick' ? (
              <form className="acft-plan-linked-editor__form" onSubmit={saveLinkedPlanningWindow}>
                <label>
                  <span>Planned start</span>
                  <FlexibleDateTimeInput
                    required
                    value={linkedPlanningDraft.plannedStartAt}
                    onChange={value => setLinkedPlanningDraft(current => ({ ...current, plannedStartAt: value }))}
                    calendarLabel="Pick planned start date"
                  />
                </label>
                <label>
                  <span>Planned finish</span>
                  <FlexibleDateTimeInput
                    required
                    value={linkedPlanningDraft.plannedFinishAt}
                    onChange={value => setLinkedPlanningDraft(current => ({ ...current, plannedFinishAt: value }))}
                    calendarLabel="Pick planned finish date"
                  />
                </label>
                {linkedPlanningFeedback ? (
                  <div className={`acft-plan-linked-editor__feedback${linkedPlanningSaving ? ' is-saving' : ' is-error'}`} role="status" aria-live="polite">
                    {linkedPlanningSaving ? <span className="personnel-scope-loading__spinner" aria-hidden="true" /> : null}
                    <span>{linkedPlanningFeedback}</span>
                  </div>
                ) : null}
                <div className="acft-plans__actions">
                  <button type="submit" disabled={linkedPlanningSaving}>
                    {linkedPlanningSaving ? 'Saving planning...' : 'Save Dates'}
                  </button>
                  {G081_PLANNING_SOURCE_TYPES.has(linkedPlanningEvent.sourceType) ? (
                    <button type="button" disabled={linkedPlanningSaving} onClick={openFullLinkedPlanning}>All Planning Fields</button>
                  ) : null}
                  <button type="button" disabled={linkedPlanningSaving} onClick={closeLinkedPlanningEditor}>Cancel</button>
                </div>
              </form>
            ) : linkedPlanningDetail.loading ? (
              <div className="acft-plan-linked-editor__loading" role="status">
                <span className="personnel-scope-loading__spinner" aria-hidden="true" />
                <span>Loading the task’s full planning record...</span>
              </div>
            ) : linkedPlanningDetail.error ? (
              <div className="acft-plan-linked-editor__feedback is-error" role="alert">
                <span>{linkedPlanningDetail.error}</span>
                <button type="button" onClick={() => setLinkedPlanningMode('quick')}>Back to Dates</button>
              </div>
            ) : fullPlanningRecord ? (
              <ResourcePlanningProvider api={api} user={user} location={selectedBase}>
              <div className="status-source-maintenance acft-plan-linked-editor__full-form">
                {linkedPlanningEvent.sourceType === 'g081Requirement' ? (
                  <G081RequirementCard
                    key={`full-requirement-${fullPlanningRecord.id}`}
                    requirement={fullPlanningRecord}
                    aircraftMds={fullPlanningAircraft.mds}
                    aircraftLocation={selectedBase}
                    planningAnchorOptions={fullPlanningAnchorOptions}
                    coverageOptions={fullPlanningCoverageOptions}
                    canManage
                    shopOptions={MILITARY_SHOP_OPTIONS}
                    laborPools={linkedPlanningDetail.data?.laborPools || []}
                    laborProfiles={linkedPlanningDetail.data?.laborProfiles || []}
                    onUpdateRequirement={updateFullG081Planning}
                    startInPlanningMode
                    planningOnly
                    onPlanningSaved={finishFullLinkedPlanning}
                    onPlanningCancelled={closeLinkedPlanningEditor}
                  />
                ) : (
                  <G081DiscrepancyCard
                    key={`full-discrepancy-${fullPlanningRecord.id}`}
                    item={fullPlanningRecord}
                    aircraftMds={fullPlanningAircraft.mds}
                    aircraftLocation={selectedBase}
                    canManage
                    shopOptions={MILITARY_SHOP_OPTIONS}
                    laborPools={linkedPlanningDetail.data?.laborPools || []}
                    laborProfiles={linkedPlanningDetail.data?.laborProfiles || []}
                    onUpdateDiscrepancy={updateFullG081Planning}
                    startInPlanningMode
                    planningOnly
                    onPlanningSaved={finishFullLinkedPlanning}
                    onPlanningCancelled={closeLinkedPlanningEditor}
                  />
                )}
              </div>
              </ResourcePlanningProvider>
            ) : (
              <div className="acft-plan-linked-editor__feedback is-error" role="alert">The selected task is no longer available.</div>
            )}
          </section>
        </div>
      ) : null}

      {editorOpen ? (
        <div className="acft-plan-modal-backdrop" role="presentation" onMouseDown={() => setEditorOpen(false)}>
          <section className="acft-plan-modal reports-panel" role="dialog" aria-modal="true" aria-labelledby="acft-plan-editor-title" onMouseDown={event => event.stopPropagation()}>
            <div className="reports-panel__header">
              <h3 id="acft-plan-editor-title">{form.id ? 'Edit Plan Event' : 'Add Plan Event'}</h3>
              <button type="button" className="acft-plan-modal__close" onClick={() => setEditorOpen(false)} aria-label="Close plan editor">x</button>
            </div>
            {feedback ? <div className="status-notification acft-plan-modal__feedback">{feedback}</div> : null}
            {linkedTailSwapEdit ? (
              <div className="status-notification acft-plan-modal__feedback">
                Editing the linked swap from tail {linkedTailSwapEdit.viewedTail}. You can update swap details here; the source tail, swap type, and receiving base are managed by the source base.
              </div>
            ) : null}
            <div className="acft-plans__form-grid">
              <label className="acft-plans__field--tail"><span>Tail # <b className="acft-plans__required">*</b></span><input value={form.tail} onChange={event => setForm({ ...form, tail: event.target.value })} list="acft-plan-tail-options" disabled={Boolean(linkedTailSwapEdit)} required /></label>
              <datalist id="acft-plan-tail-options">{tails.map(tail => <option key={tail} value={tail} />)}</datalist>
              <datalist id="acft-plan-swap-tail-options">{swapTailOptions.map(tail => <option key={tail} value={tail} />)}</datalist>
              <datalist id="acft-plan-swap-location-options">{locationOptions.map(location => <option key={location} value={location} />)}</datalist>
              <label className="acft-plans__field--type"><span>Type <b className="acft-plans__required">*</b></span><select value={form.eventType} onChange={event => updateEventType(event.target.value)} disabled={Boolean(linkedTailSwapEdit)} required>{eventTypes.map(type => <option key={type} value={type}>{type}</option>)}</select></label>
              {form.eventType === 'Inspection' ? (
                <>
                  <label><span>Inspection <b className="acft-plans__required">*</b></span><select value={form.inspectionSubtype} onChange={event => setForm({ ...form, inspectionSubtype: event.target.value })} required>{inspectionSubtypes.map(type => <option key={type} value={type}>{type}</option>)}</select></label>
                  {form.inspectionSubtype === 'Other' ? <label><span>Other Inspection <b className="acft-plans__required">*</b></span><input value={form.inspectionSubtypeOther} onChange={event => setForm({ ...form, inspectionSubtypeOther: event.target.value })} required /></label> : null}
                  <label><span>Original Due <b className="acft-plans__required">*</b></span><input type="date" value={form.originalDueDate} disabled={Boolean(form.id && form.originalDueDate)} onChange={event => setForm({ ...form, originalDueDate: event.target.value })} required /></label>
                  <label><span>Estimated Hours <b className="acft-plans__required">*</b></span><input type="number" min="0.5" step="0.5" value={form.estimatedDurationHours} onChange={event => setForm({ ...form, estimatedDurationHours: event.target.value })} required /></label>
                </>
              ) : null}
              {formIsTailSwap ? (
                <>
                  <label><span>Projected Swap Date <b className="acft-plans__required">*</b></span><input type="date" value={form.startDate} onChange={event => updateSwapDate(event.target.value)} required /></label>
                  <label><span>Swap With/For Tail <b className="acft-plans__required">*</b></span><input value={form.swapWithTail} onChange={event => setForm({ ...form, swapWithTail: event.target.value.toUpperCase() })} onBlur={event => setForm(current => ({ ...current, swapWithTail: normalizePlanTailDisplay(event.target.value) }))} list="acft-plan-swap-tail-options" placeholder="YY-#### or TBD" pattern="(?:\\d{2}-\\d{4}|TBD)" required /></label>
                  <label className="acft-plans__field--swap-base"><span>Swap With/For Base</span><input value={form.swapWithLocation} onChange={event => setForm({ ...form, swapWithLocation: event.target.value })} list="acft-plan-swap-location-options" placeholder="Select base" disabled={Boolean(linkedTailSwapEdit)} /></label>
                </>
              ) : (
                <>
                  <label><span>Planned Start <b className="acft-plans__required">*</b></span><input type="date" value={form.startDate} onChange={event => setForm({ ...form, startDate: event.target.value })} required /></label>
                  <label><span>Planned Finish <b className="acft-plans__required">*</b></span><input type="date" value={form.endDate} onChange={event => setForm({ ...form, endDate: event.target.value })} required /></label>
                </>
              )}
              <label><span>Actual Start</span><input type="date" value={form.actualStartDate} onChange={event => setForm({ ...form, actualStartDate: event.target.value })} /></label>
              <label><span>Actual Finish</span><input type="date" value={form.actualFinishDate} onChange={event => setForm({ ...form, actualFinishDate: event.target.value })} /></label>
              <label className={formIsTailSwap ? 'acft-plans__field--swap-location' : ''}><span>{formIsTailSwap ? 'Projected Swap Location' : 'Location'}</span><input value={form.planLocation} onChange={event => setForm({ ...form, planLocation: event.target.value })} /></label>
              {formIsTailSwap ? (
                <label className="acft-plans__checkbox acft-plans__checkbox--swap-location"><input type="checkbox" checked={Boolean(form.swapForeignOsMasked)} onChange={event => setForm({ ...form, swapForeignOsMasked: event.target.checked })} /><span>Foreign OS masked</span></label>
              ) : null}
              <label className="acft-plans__wide"><span>Remarks</span><input value={form.remarks} onChange={event => setForm({ ...form, remarks: event.target.value })} /></label>
            </div>
            <div className="acft-plans__actions">
              <button type="button" onClick={savePlan} disabled={saving}>{saving ? 'Saving...' : form.id ? 'Update Plan Event' : 'Add Plan Event'}</button>
              {form.id && !linkedTailSwapEdit ? <button type="button" onClick={() => deletePlan(form)}>Delete</button> : null}
              {!linkedTailSwapEdit ? <button type="button" onClick={() => setForm(createEmptyForm(form.tail, form.eventType))}>Clear</button> : null}
              <button type="button" onClick={() => setEditorOpen(false)}>Cancel</button>
            </div>
          </section>
        </div>
      ) : null}

      <section className="acft-plans__summary">
        <div><span>Total Plan Items</span><strong>{events.length}</strong></div>
        <div><span>Weekly Flying</span><strong>{weeklyFlyingEvents.length}</strong></div>
        <div><span>Status Conflicts</span><strong>{conflicts.length}</strong></div>
        <div><span>Commander Sign-Off</span><strong>{signoff ? 'Signed' : 'Open'}</strong></div>
      </section>

      <section className="acft-plans__legend" aria-label="Highlight long-range aircraft plans by category">
        <strong>G081 filter: only items marked Readiness Driver appear here or on Calendar.</strong>
        {ACFT_PLAN_FILTERS.map(([category, label, swatchClass]) => (
          <button
            key={category}
            type="button"
            className={`calendar-filter-toggle${activePlanCategories.includes(category) ? ' is-selected' : ''}`}
            aria-pressed={activePlanCategories.includes(category)}
            onClick={() => setActivePlanCategories(current => (
              current.includes(category)
                ? current.filter(item => item !== category)
                : [...current, category]
            ))}
            title={`Highlight ${label} plans in the Long-Range Plan`}
          >
            <i className={swatchClass} />
            {label}
          </button>
        ))}
        {activePlanCategories.length ? (
          <button
            type="button"
            className="calendar-filter-clear"
            onClick={() => setActivePlanCategories([])}
          >
            Clear highlights
          </button>
        ) : null}
      </section>

      <section className="reports-panel">
        <div className="reports-panel__header">
          <h3>Long-Range Plan</h3>
        </div>
        <div className="reports-table-shell acft-plans__monthly-shell">
          <div
            className="acft-plans__monthly-sticky-header"
            aria-hidden="true"
            style={monthlyGridStyle}
          >
            <div className="acft-plans__monthly-year-row">
              <div>Window</div>
              {monthYearBands.map(band => <div key={band.year} style={{ gridColumn: `span ${band.count}` }}>{band.year}</div>)}
            </div>
            <div className="acft-plans__monthly-month-row">
              <div>Tail #</div>
              {months.map(month => <div key={`${month.year}-${month.index}`}>{month.label}</div>)}
            </div>
          </div>
          <table className="reports-table acft-plans__monthly" style={monthlyGridStyle}>
            <thead className="acft-plans__monthly-table-head">
              <tr className="acft-plans__year-band">
                <th>Window</th>
                {monthYearBands.map(band => <th key={band.year} colSpan={band.count}>{band.year}</th>)}
              </tr>
              <tr>
                <th>Tail #</th>
                {months.map(month => <th key={`${month.year}-${month.index}`}>{month.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {groupedTails.map(group => (
                <React.Fragment key={group.key}>
                  <tr className="acft-plans__scope-row">
                    <th colSpan={months.length + 1}>{group.label} <span>{group.tails.length}</span></th>
                  </tr>
                  {group.tails.map(tail => {
                    const status = statusByTail.get(tail);
                    const tailEvents = tailScopedEvents.filter(event => event.tail === tail);
                    const rowLayout = layoutTailPlanEvents(tailEvents, months);
                    const rowConnectors = linkedPlanConnectors(tailEvents, rowLayout.byId);
                    const activeRowConnectors = rowConnectors.filter(connector => (
                      activePlanEventId
                      && (connector.anchorId === activePlanEventId || connector.childId === activePlanEventId)
                    ));
                    const activeLinkedEventIds = new Set(activeRowConnectors.flatMap(connector => [connector.anchorId, connector.childId]));
                    return (
                      <tr key={tail}>
                        <td>
                          <strong>{tail}</strong>
                          <span className={`acft-plan-status acft-plan-status--${statusTone(status?.status)}`}>{status?.status || 'No Status'}</span>
                          <small className="acft-plan-tail-context">
                            {hsOsValue(status) === 'OS' ? (
                              <>OS | <OffstationLocationDisplay value={status?.osLocation} /></>
                            ) : aircraftScopeLabel(status)}
                          </small>
                        </td>
                        <td colSpan={months.length} className="acft-plan-row-grid-cell">
                          <div
                            className="acft-plan-row-grid"
                            style={{
                              ...monthlyGridStyle,
                              '--acft-plan-row-height': `${rowLayout.height}px`,
                            }}
                          >
                            <div className="acft-plan-event-layer">
                              {rowLayout.items.map(({ event, gridColumn, gridRow }) => {
                                const dependencyLabel = planDependencyLabel(event, rowLayout.byId);
                                const swapContext = tailSwapContextText(event);
                                const canEditEvent = Boolean(
                                  (canManageAcftPlans && ['plan', 'linkedTailSwap'].includes(event.sourceType))
                                  || (event.canEditPlanning && LINKED_PLANNING_SOURCE_TYPES.has(event.sourceType))
                                );
                                return (
                                  <button
                                    key={event.id}
                                    type="button"
                                    className={[
                                      'acft-plan-block',
                                      `acft-plan-block--${planSourceTone(event)}`,
                                      planRiskClass(event),
                                      dependencyLabel ? 'has-plan-link' : '',
                                      activeLinkedEventIds.has(event.id) ? 'is-plan-link-active' : '',
                                      canEditEvent ? 'is-editable' : '',
                                      activePlanCategories.length
                                        ? (planMatchesLegendSelection(event, activePlanCategories) ? 'is-legend-highlighted' : 'is-legend-muted')
                                        : '',
                                    ].filter(Boolean).join(' ')}
                                    style={{
                                      gridColumn,
                                      gridRow,
                                    }}
                                    onMouseEnter={() => setActivePlanEventId(event.id)}
                                    onMouseLeave={() => setActivePlanEventId(current => (current === event.id ? '' : current))}
                                    onFocus={() => setActivePlanEventId(event.id)}
                                    onBlur={() => setActivePlanEventId(current => (current === event.id ? '' : current))}
                                    onClick={() => {
                                      if (canManageAcftPlans && ['plan', 'linkedTailSwap'].includes(event.sourceType)) {
                                        openEventEditor(event);
                                      } else if (event.canEditPlanning) {
                                        openLinkedPlanningEditor(event);
                                      }
                                    }}
                                    aria-disabled={!canEditEvent}
                                    title={`${planEventTitle(event)}: ${planWindowLabel(event)}${swapContext ? `\n${swapContext}` : ''}${planDueLabel(event) ? `\n${planDueLabel(event)}` : ''}${dependencyLabel ? `\n${dependencyLabel}` : ''}${scheduleWarningText(event) ? `\n${scheduleWarningText(event)}` : ''}${canEditEvent ? '\nClick to edit planning.' : ''}`}
                                  >
                                    <strong>{planEventTitle(event)}</strong>
                                    {swapContext ? <small>{swapContext}</small> : null}
                                    <small>{planWindowLabel(event)}{planDueLabel(event) ? ` | ${planDueLabel(event)}` : ''}</small>
                                    {dependencyLabel ? <small className="acft-plan-move-with">{dependencyLabel}</small> : null}
                                  </button>
                                );
                              })}
                            </div>
                            {months.map((month, monthPosition) => {
                              const monthEvents = monthEventsForTail(tailEvents, month);
                              return (
                                <div
                                  key={`${tail}-${month.year}-${month.index}`}
                                  className={`acft-plan-month-cell${monthPosition === 0 ? ' is-first' : ''}`}
                                >
                                  {canManageAcftPlans ? (
                                    <button
                                      type="button"
                                      className={`acft-plan-empty-cell${monthEvents.length ? ' has-month-events' : ''}`}
                                      onClick={() => openMonthEditor(tail, month)}
                                      title={`Add ${tail} plan for ${month.label}`}
                                    >
                                      + Add
                                    </button>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {tails.length === 0 ? (
                <tr><td colSpan={months.length + 1}>No aircraft status rows or plan events found for this location.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="reports-panel">
        <div className="reports-panel__header">
          <h3>Weekly Flying Plan</h3>
          <div className="acft-plans__week-controls">
            <label><span>Week Of</span><input type="date" value={selectedWeekStart} onChange={event => setSelectedWeekStart(dateKey(startOfWeekMonday(parseDateKey(event.target.value) || new Date())))} /></label>
            <button type="button" onClick={() => setSelectedWeekStart(dateKey(startOfWeekMonday(new Date())))}>This Week</button>
            <button type="button" onClick={signWeek} disabled={!canSignWeekPlan || signing}>{signing ? 'Signing...' : 'Commander Sign-Off'}</button>
          </div>
        </div>
        <div className="acft-plans__signoff">
          {signoff ? (
            <>
              <span>
                Signed by {signoff.signedByName || signoff.signedByEmail}
                {signoff.signedByRole ? ` (${signoff.signedByRole})` : ''}
                {' '}at {formatDateTime(signoff.signedAt)}
              </span>
              <strong className={hasChangesSinceSignoff ? 'acft-plans__review-state acft-plans__review-state--pending' : 'acft-plans__review-state acft-plans__review-state--signed'}>
                {hasChangesSinceSignoff ? 'Signed, changes pending review' : 'Signed, no changes since review'}
              </strong>
            </>
          ) : (
            <span>Awaiting commander sign-off for weekly flying plan {selectedWeekStart} to {weekEndKey}.</span>
          )}
          {commanders.length ? <span>Commander roster: {commanders.map(row => `${row.Rank || ''} ${row['First Name'] || ''} ${row['Last Name'] || ''}`.trim()).join(', ')}</span> : null}
        </div>
        <div className="acft-plans__review-panel">
          <div className="reports-panel__header">
            <h4>Changes Since Last Commander Flying Plan Review</h4>
            <span>{reviewChanges.length} change{reviewChanges.length === 1 ? '' : 's'}</span>
          </div>
          {signoff ? (
            reviewChanges.length ? (
              <div className="reports-table-shell">
                <table className="reports-table acft-plans__review-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Tail</th>
                      <th>Type</th>
                      <th>Dates</th>
                      <th>Changed By</th>
                      <th>Changed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewChanges.map(change => (
                      <tr key={change.id}>
                        <td><span className={`acft-plan-change acft-plan-change--${String(change.actionLabel || '').toLowerCase()}`}>{change.actionLabel || change.action}</span></td>
                        <td>{change.tail || '--'}</td>
                        <td>{change.eventType || '--'}</td>
                        <td>{formatChangeDates(change)}</td>
                        <td>{change.changedBy || '--'}</td>
                        <td>{formatDateTime(change.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No aircraft plan changes have been made since the latest commander review.</p>
            )
          ) : (
            <p>No commander flying plan review has been signed for this week yet.</p>
          )}
        </div>
        <div className="reports-table-shell">
          <table className="reports-table acft-plans__weekly">
            <thead>
              <tr>
                <th>Tail #</th>
                {weekDays.map(day => <th key={dateKey(day)}>{day.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' })}</th>)}
              </tr>
            </thead>
            <tbody>
              {tails.map(tail => {
                const hasAny = weeklyFlyingEvents.some(event => event.tail === tail);
                if (!hasAny) return null;
                const status = statusByTail.get(tail);
                return (
                  <tr key={`week-${tail}`}>
                    <td>
                      <strong>{tail}</strong>
                      <span className={`acft-plan-status acft-plan-status--${statusTone(status?.status)}`}>{status?.status || 'No Status'}</span>
                    </td>
                    {weekDays.map(day => {
                      const dayEvents = weeklyFlyingEvents.filter(event => {
                        if (event.tail !== tail) return false;
                        const start = parseDateKey(event.startDate);
                        const end = parseDateKey(event.endDate);
                        return start && end && rangesOverlap(start, end, day, day);
                      });
                      return (
                        <td key={`${tail}-${dateKey(day)}`}>
                          {dayEvents.map(event => (
                            <div
                              key={event.id}
                              className={[
                                'acft-plan-week-card',
                                `acft-plan-week-card--${planSourceTone(event)}`,
                                planRiskClass(event),
                              ].filter(Boolean).join(' ')}
                            >
                              {canManageAcftPlans && event.sourceType === 'plan' ? (
                                <button type="button" onClick={() => openEventEditor(event)}>{planEventTitle(event)}</button>
                              ) : (
                                <strong>{planEventTitle(event)}</strong>
                              )}
                              <span>{planSourceLabel(event)} | {planWindowLabel(event)}{planDueLabel(event) ? ` | ${planDueLabel(event)}` : ''}</span>
                              {planDurationLabel(event) ? <span>{planDurationLabel(event)}</span> : null}
                              {event.reviewRequired ? <strong>Review start vs return</strong> : null}
                              {statusTone(status?.status) === 'nmc' ? <strong>Status conflict</strong> : null}
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {weeklyFlyingEvents.length === 0 ? (
                <tr><td colSpan={8}>No local-use flying plan events overlap this week.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="acft-plans__rollover">
          <strong>Daily Fly Schedule handoff</strong>
          <span>{weeklyFlyingEvents.length} local-use flying plan event(s) in this week. These are candidates for the daily Fly Schedule once sortie times and call signs are defined.</span>
        </div>
      </section>

      <section className="reports-panel">
        <div className="reports-panel__header">
          <h3>Plan Events</h3>
          <span>Full list of manual events and linked planning items</span>
        </div>
        <div className="reports-table-shell">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Tail</th>
                <th>Type</th>
                <th>Source</th>
                <th>Due</th>
                <th>Planned</th>
                <th>Duration</th>
                <th>Actual</th>
                <th>Location</th>
                <th>Remarks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tailScopedEvents.map(event => {
                const swapContext = tailSwapContextText(event);
                return (
                  <tr key={event.id}>
                    <td>{event.tail}</td>
                    <td>{planEventTitle(event)}</td>
                    <td><span className={`acft-plan-source-pill acft-plan-source-pill--${planSourceTone(event)}`}>{planSourceLabel(event)}</span></td>
                    <td>{event.originalDueDate ? formatShortDate(event.originalDueDate) : '--'}</td>
                    <td>{formatShortDate(event.startDate)} - {formatShortDate(event.endDate)}</td>
                    <td>{planDurationLabel(event) || '--'}</td>
                    <td>{event.actualStartDate ? `${formatShortDate(event.actualStartDate)} - ${formatShortDate(event.actualFinishDate)}` : '--'}</td>
                    <td>{event.sourceType === 'estimatedReturn' || isTailSwapEventType(event.eventType) ? <OffstationLocationDisplay value={event.planLocation} emptyLabel="--" /> : (event.planLocation || '--')}</td>
                    <td>
                      {event.reviewRequired ? (
                        <>
                          <span className="acft-plan-review-note">{scheduleWarningText(event)}</span>
                          {swapContext ? <span>{swapContext}</span> : null}
                          {event.remarks ? <span>{event.remarks}</span> : null}
                        </>
                      ) : (
                        <>
                          {swapContext ? <span>{swapContext}</span> : null}
                          <span>{event.remarks || (!swapContext ? '--' : '')}</span>
                        </>
                      )}
                    </td>
                    <td className="status-table__actions">
                      {canManageAcftPlans && event.sourceType === 'plan' ? (
                        <>
                          <button type="button" onClick={() => openEventEditor(event)}>Edit</button>
                          <button type="button" onClick={() => deletePlan(event)}>Delete</button>
                        </>
                      ) : canManageAcftPlans && event.sourceType === 'linkedTailSwap' ? (
                        <button type="button" onClick={() => openEventEditor(event)}>Edit</button>
                      ) : event.canEditPlanning && LINKED_PLANNING_SOURCE_TYPES.has(event.sourceType) ? (
                        <button type="button" onClick={() => openLinkedPlanningEditor(event)}>Edit dates</button>
                      ) : (
                        <span>{event.sourceLabel || 'Read-only'}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {tailScopedEvents.length === 0 ? <tr><td colSpan={10}>No plan items yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
